/**
 * Peso del día, tendencia y cambio semanal (Req. 16; design.md, 8.5), el
 * rango válido de un pesaje (Req. 15.6) y la pregunta de unidad equivocada
 * (Req. 15.4; design.md, 5.4).
 *
 * Nada de esto se guarda: sale de los pesajes cada vez, así que editar o
 * borrar un pesaje cambia la tendencia sin migrar nada.
 */

import { diferenciaDias, esFechaLocal, sumarDias, sumarMeses, type FechaLocal } from './dates';
import { formatearNumero, leerDecimal } from './numbers';
import { esHoraLocal } from './time';
import { aKg, desdeKg, formatearPeso, otraUnidadPeso, type UnidadPeso } from './units';

/** Rango válido de un pesaje, en kg (Req. 15.6). */
export const PESO_MIN_KG = 20;
export const PESO_MAX_KG = 300;
/** La tendencia de un día promedia ese día y los 6 anteriores (Req. 16.2). */
export const DIAS_TENDENCIA = 7;
/** Diferencia contra la referencia que hace preguntar la unidad (Req. 15.4). */
export const DIFERENCIA_UNIDAD_KG = 5;

/** Lo que estos cálculos leen de un pesaje. */
export interface PesajeBase {
  id: string;
  fecha: FechaLocal;
  momento: string;
  pesoKg: number;
  scaleId: string | null;
  creadoEn?: string;
}

/**
 * Las básculas que cuentan para la tendencia (Req. 14.4): null = todas,
 * también los pesajes sin báscula; un id = solo esa.
 */
export type BasculaDeTendencia = string | null;

export function cuentaEnTendencia(p: Pick<PesajeBase, 'scaleId'>, bascula: BasculaDeTendencia): boolean {
  return bascula === null || p.scaleId === bascula;
}

/**
 * ¿Se puede usar este pesaje? Uno que llegó en un respaldo editado a mano
 * puede traer la fecha, la hora, el momento o el peso dañados (al restaurar
 * solo se avisa): no se muestra ni cuenta, y sigue guardado tal cual. Antes
 * rompía Hoy, Cuerpo y Pesajes (revisión de la 0.4.0).
 */
export function pesajeLegible(p: { fecha: unknown; hora?: unknown; momento: unknown; pesoKg: unknown }): boolean {
  return (
    esFechaLocal(p.fecha) &&
    (p.hora === undefined || esHoraLocal(p.hora)) &&
    typeof p.momento === 'string' &&
    !Number.isNaN(Date.parse(p.momento)) &&
    typeof p.pesoKg === 'number' &&
    Number.isFinite(p.pesoKg) &&
    p.pesoKg > 0
  );
}

const instante = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
};

/** true si `a` se pesó antes que `b` (por su momento; a la misma hora, el que se capturó primero). */
function anterior(a: PesajeBase, b: PesajeBase): boolean {
  const diferencia = instante(a.momento) - instante(b.momento);
  if (diferencia !== 0) return diferencia < 0;
  return (a.creadoEn ?? '') < (b.creadoEn ?? '');
}

/**
 * Peso del día (Req. 16.1): el primer pesaje de cada día, por su momento,
 * entre los que cuentan para la tendencia.
 */
export function pesosPorDia(pesajes: readonly PesajeBase[], bascula: BasculaDeTendencia): Map<FechaLocal, number> {
  const primeros = new Map<FechaLocal, PesajeBase>();
  for (const p of pesajes) {
    if (!cuentaEnTendencia(p, bascula)) continue;
    const actual = primeros.get(p.fecha);
    if (actual === undefined || anterior(p, actual)) primeros.set(p.fecha, p);
  }
  return new Map([...primeros].map(([fecha, p]) => [fecha, p.pesoKg]));
}

/**
 * Tendencia de un día (Req. 16.2): el promedio de los pesos del día de ese
 * día y los 6 anteriores que existan; null si no hay ninguno.
 */
export function tendenciaDe(pesos: ReadonlyMap<FechaLocal, number>, fecha: FechaLocal): number | null {
  let suma = 0;
  let dias = 0;
  for (let i = 0; i < DIAS_TENDENCIA; i++) {
    const kg = pesos.get(sumarDias(fecha, -i));
    if (kg !== undefined) {
      suma += kg;
      dias += 1;
    }
  }
  return dias === 0 ? null : suma / dias;
}

/** Cambio de la tendencia contra la de 7 días antes (Req. 16.3); null si falta alguna. */
export function cambioSemanalDe(pesos: ReadonlyMap<FechaLocal, number>, fecha: FechaLocal): number | null {
  const ahora = tendenciaDe(pesos, fecha);
  const antes = tendenciaDe(pesos, sumarDias(fecha, -DIAS_TENDENCIA));
  return ahora === null || antes === null ? null : ahora - antes;
}

/** El último pesaje hasta hoy, de cualquier báscula (Req. 16.3), por su momento. */
export function ultimoPesaje<T extends PesajeBase>(pesajes: readonly T[], hoy: FechaLocal): T | null {
  let ultimo: T | null = null;
  for (const p of pesajes) {
    if (p.fecha > hoy) continue;
    if (ultimo === null || anterior(ultimo, p)) ultimo = p;
  }
  return ultimo;
}

export interface ResumenDePeso<T extends PesajeBase = PesajeBase> {
  /** El último pesaje, de cualquier báscula; null si no hay. */
  ultimo: T | null;
  /** La tendencia de hoy; null si en los últimos 7 días no hay pesajes que cuenten. */
  tendenciaKg: number | null;
  /** Contra la de hace 7 días; null si falta alguna de las dos. */
  cambioSemanalKg: number | null;
  /** El último día con un peso que cuenta para la tendencia. */
  ultimoDiaConPeso: FechaLocal | null;
  /** Cuántos pesajes hay hasta hoy, de todas las básculas. */
  total: number;
}

/** Lo que muestran Cuerpo y Hoy (Req. 16.3, 24.1). */
export function resumenDePeso<T extends PesajeBase>(
  pesajes: readonly T[],
  bascula: BasculaDeTendencia,
  hoy: FechaLocal,
): ResumenDePeso<T> {
  const hastaHoy = pesajes.filter((p) => p.fecha <= hoy);
  const pesos = pesosPorDia(hastaHoy, bascula);
  const dias = [...pesos.keys()].sort();
  return {
    ultimo: ultimoPesaje(hastaHoy, hoy),
    tendenciaKg: tendenciaDe(pesos, hoy),
    cambioSemanalKg: cambioSemanalDe(pesos, hoy),
    ultimoDiaConPeso: dias[dias.length - 1] ?? null,
    total: hastaHoy.length,
  };
}

// ---------------------------------------------------------------------------
// Gráfica (Req. 16.4)
// ---------------------------------------------------------------------------

export type Rango = '1m' | '3m' | '6m' | '1a' | 'todo';

export const RANGOS: readonly { valor: Rango; corto: string; nombre: string; meses: number | null }[] = [
  { valor: '1m', corto: '1 m', nombre: '1 mes', meses: 1 },
  { valor: '3m', corto: '3 m', nombre: '3 meses', meses: 3 },
  { valor: '6m', corto: '6 m', nombre: '6 meses', meses: 6 },
  { valor: '1a', corto: '1 año', nombre: '1 año', meses: 12 },
  { valor: 'todo', corto: 'Todo', nombre: 'Todo', meses: null },
];

/** El primer día de un rango que termina hoy. "Todo" empieza en el primer dato. */
export function inicioDeRango(rango: Rango, hoy: FechaLocal, primerDato: FechaLocal | null): FechaLocal {
  const meses = RANGOS.find((r) => r.valor === rango)?.meses ?? null;
  if (meses === null) return primerDato !== null && primerDato < hoy ? primerDato : hoy;
  return sumarDias(sumarMeses(hoy, -meses), 1);
}

export interface PuntoDeSerie {
  fecha: FechaLocal;
  valor: number;
}

export interface SerieDePeso {
  /** El peso de cada día con pesaje (puntos). */
  pesos: PuntoDeSerie[];
  /** La tendencia de cada día que la tiene (línea); se corta donde no hay. */
  tendencia: PuntoDeSerie[];
}

/** Los puntos y la línea de un periodo, en kg. */
export function serieDePeso(
  pesajes: readonly PesajeBase[],
  bascula: BasculaDeTendencia,
  desde: FechaLocal,
  hasta: FechaLocal,
): SerieDePeso {
  const pesos = pesosPorDia(pesajes, bascula);
  const enRango = [...pesos.entries()]
    .filter(([fecha]) => fecha >= desde && fecha <= hasta)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([fecha, valor]) => ({ fecha, valor }));
  const tendencia: PuntoDeSerie[] = [];
  const total = diferenciaDias(desde, hasta);
  for (let i = 0; i <= total; i++) {
    const fecha = sumarDias(desde, i);
    const valor = tendenciaDe(pesos, fecha);
    if (valor !== null) tendencia.push({ fecha, valor });
  }
  return { pesos: enRango, tendencia };
}

// ---------------------------------------------------------------------------
// Captura (Req. 15)
// ---------------------------------------------------------------------------

/** "20 y 300 kg" o "44.1 y 661.3 lb": los extremos que de verdad se aceptan. */
export function rangoDePeso(unidad: UnidadPeso): string {
  const min = Math.ceil(desdeKg(PESO_MIN_KG, unidad) * 10) / 10;
  const max = Math.floor(desdeKg(PESO_MAX_KG, unidad) * 10) / 10;
  const texto = (v: number) => formatearNumero(v, { decimales: 1, fijos: false });
  return `${texto(min)} y ${texto(max)} ${unidad}`;
}

export type ResultadoPeso = { valido: true; valor: number; pesoKg: number } | { valido: false; error: string };

/** El peso escrito en kg o lb: el valor y su equivalente en kg, o por qué no sirve (Req. 15.6). */
export function validarPeso(texto: string, unidad: UnidadPeso): ResultadoPeso {
  if (texto.trim() === '') return { valido: false, error: 'Escribe tu peso.' };
  const valor = leerDecimal(texto);
  if (valor === null || valor <= 0) return { valido: false, error: 'Escribe el peso con números, por ejemplo 70.5.' };
  const pesoKg = aKg(valor, unidad);
  if (pesoKg < PESO_MIN_KG || pesoKg > PESO_MAX_KG) {
    return { valido: false, error: `El peso debe estar entre ${rangoDePeso(unidad)}.` };
  }
  return { valido: true, valor, pesoKg };
}

/**
 * Con qué se compara un pesaje para preguntar la unidad: la tendencia de su
 * día sin contar ese mismo pesaje (al editarlo) y, sin tendencia, el peso del
 * día más cercano a esa fecha de cualquier báscula. null si no hay pesajes.
 */
export function referenciaParaUnidad(
  pesajes: readonly PesajeBase[],
  bascula: BasculaDeTendencia,
  fecha: FechaLocal,
  sinId: string | null,
): number | null {
  const otros = sinId === null ? pesajes : pesajes.filter((p) => p.id !== sinId);
  const tendencia = tendenciaDe(pesosPorDia(otros, bascula), fecha);
  if (tendencia !== null) return tendencia;
  let cercano: { dias: number; kg: number } | null = null;
  for (const [dia, kg] of pesosPorDia(otros, null)) {
    const dias = Math.abs(diferenciaDias(dia, fecha));
    if (cercano === null || dias < cercano.dias) cercano = { dias, kg };
  }
  return cercano?.kg ?? null;
}

export interface PreguntaDeUnidad {
  valor: number;
  /** La unidad elegida al escribir. */
  escrita: UnidadPeso;
  /** La otra, con la que queda cerca de la referencia. */
  otra: UnidadPeso;
  kgEscrita: number;
  kgOtra: number;
}

/**
 * design.md, 5.4: se pregunta si con la unidad elegida queda a más de 5 kg de
 * la referencia y con la otra a 5 kg o menos. Sin referencia no se pregunta.
 * Solo pregunta: nunca cambia la unidad por su cuenta (Req. 5.10).
 */
export function preguntaDeUnidad(valor: number, unidad: UnidadPeso, referenciaKg: number | null): PreguntaDeUnidad | null {
  if (referenciaKg === null) return null;
  const otra = otraUnidadPeso(unidad);
  const kgEscrita = aKg(valor, unidad);
  const kgOtra = aKg(valor, otra);
  const lejos = Math.abs(kgEscrita - referenciaKg) > DIFERENCIA_UNIDAD_KG;
  const cerca = Math.abs(kgOtra - referenciaKg) <= DIFERENCIA_UNIDAD_KG;
  return lejos && cerca ? { valor, escrita: unidad, otra, kgEscrita, kgOtra } : null;
}

/** "¿Es 154 lb (69.9 kg) o 154 kg?": lo que sería con la otra unidad primero. */
export function textoPreguntaDeUnidad(p: PreguntaDeUnidad): string {
  const valor = formatearNumero(p.valor, { decimales: 2, fijos: false });
  const enKg = p.otra === 'lb' ? ` (${formatearPeso(p.kgOtra, 'kg')})` : '';
  const escritaEnKg = p.escrita === 'lb' ? ` (${formatearPeso(p.kgEscrita, 'kg')})` : '';
  return `¿Es ${valor} ${p.otra}${enKg} o ${valor} ${p.escrita}${escritaEnKg}?`;
}

/** "+0.4 kg", "−1.2 lb" o "0.0 kg": un cambio con su signo, en la unidad elegida. */
export function formatearCambio(kg: number, unidad: UnidadPeso): string {
  const texto = formatearPeso(kg, unidad);
  const redondeado = Number(formatearNumero(desdeKg(kg, unidad), { decimales: 1 }).replace(/[^0-9.]/g, ''));
  return kg > 0 && redondeado > 0 ? `+${texto}` : texto;
}
